/**
 * Attendance bands editor — Advanced tab (WSRADM-0039).
 * Full-list Save; Reset sends `{ bands: [] }`. Takes effect instantly — no rebuild.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { putAttendanceBands, type AttendanceBandsDto } from '@/api/configuration/attendanceBands';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { Icon } from '@/theme/Icon';
import {
  bandsEqual,
  emptyDraft,
  fromDto,
  HOUR_OPTIONS,
  MAX_BANDS,
  toPutBody,
  validate,
  type BandDraft,
  type BandIssue,
} from './attendanceBandsModel';

export interface AttendanceBandsSettingsProps {
  data: AttendanceBandsDto;
  onSaved: (next: AttendanceBandsDto) => void;
}

function issueFor(
  issues: readonly BandIssue[],
  index: number,
  kinds: BandIssue['kind'][],
): BandIssue | undefined {
  return issues.find((iss) => {
    if (iss.kind === 'too-many') return false;
    return iss.index === index && kinds.includes(iss.kind);
  });
}

export function AttendanceBandsSettings({ data, onSaved }: AttendanceBandsSettingsProps) {
  const { t } = useCopy();
  const [drafts, setDrafts] = useState<BandDraft[]>(() => fromDto(data.bands));
  const [saving, setSaving] = useState(false);
  // The caught save error (INVALID_ATTENDANCE_BANDS included — its server message
  // is the reason `ErrorDetails` shows), else `null`.
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    setDrafts(fromDto(data.bands));
    setSaveFailure(null);
  }, [data]);

  const issues = useMemo(() => validate(drafts), [drafts]);
  const putBody = useMemo(() => toPutBody(drafts), [drafts]);
  const dirty = !bandsEqual(putBody, data.bands);
  // Empty body is the Reset sentinel — Save must never send { bands: [] }.
  const saveDisabled =
    saving || issues.length > 0 || !dirty || putBody.length === 0 || drafts.length === 0;

  function patchDraft(id: string, patch: Partial<BandDraft>) {
    setDrafts((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    setSaveSuccess(false);
    setSaveFailure(null);
  }

  function removeDraft(id: string) {
    setDrafts((prev) => prev.filter((row) => row.id !== id));
    setSaveSuccess(false);
    setSaveFailure(null);
  }

  function addDraft() {
    if (drafts.length >= MAX_BANDS) return;
    setDrafts((prev) => [...prev, emptyDraft()]);
    setSaveSuccess(false);
    setSaveFailure(null);
  }

  function labelError(index: number): string | undefined {
    const blank = issueFor(issues, index, ['label-blank']);
    if (blank) return t('config.bands.err.blank', 'Enter a label.');
    const long = issueFor(issues, index, ['label-long']);
    if (long) return t('config.bands.err.long', 'Label must be 60 characters or fewer.');
    const reserved = issueFor(issues, index, ['label-reserved']);
    if (reserved) {
      return t('config.bands.err.reserved', 'Other and Unknown time are reserved.');
    }
    const dup = issueFor(issues, index, ['label-duplicate']);
    if (dup) return t('config.bands.err.duplicate', 'Label must be unique.');
    return undefined;
  }

  function hoursError(index: number): string | undefined {
    const missing = issueFor(issues, index, ['hours-missing']);
    if (missing) return t('config.bands.err.hoursMissing', 'Choose from and to hours.');
    const order = issueFor(issues, index, ['hours-order']);
    if (order) {
      return t('config.bands.err.hoursOrder', 'From must be less than to.');
    }
    const overlap = issues.find(
      (iss): iss is Extract<BandIssue, { kind: 'overlap' }> =>
        iss.kind === 'overlap' && iss.index === index,
    );
    if (overlap) {
      const other = drafts[overlap.withIndex];
      const otherLabel = other?.label.trim() || t('config.bands.label', 'Label');
      return t('config.bands.err.overlap', 'Overlaps with {{label}}', { label: otherLabel });
    }
    return undefined;
  }

  async function onSave() {
    if (saveDisabled) return;
    setSaving(true);
    setSaveFailure(null);
    setSaveSuccess(false);
    try {
      const response = await putAttendanceBands(putBody);
      onSaved(response);
      setDrafts(fromDto(response.bands));
      setSaveSuccess(true);
    } catch (err) {
      setSaveFailure(err);
    } finally {
      setSaving(false);
    }
  }

  async function onResetConfirm() {
    setResetOpen(false);
    if (saving) return;
    setSaving(true);
    setSaveFailure(null);
    setSaveSuccess(false);
    try {
      const response = await putAttendanceBands([]);
      onSaved(response);
      setDrafts(fromDto(response.bands));
      setSaveSuccess(true);
    } catch (err) {
      setSaveFailure(err);
    } finally {
      setSaving(false);
    }
  }

  const tooMany = issues.some((iss) => iss.kind === 'too-many');

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 4 }} component="section">
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <Typography variant="h2" component="h2">
          {t('config.bands.title', 'Attendance bands')}
        </Typography>
        {data.default ? (
          <Chip size="small" label={t('config.bands.defaultChip', 'Platform default')} />
        ) : null}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(
          'config.bands.help',
          "Bands split each day's check-ins for the dashboard. Hours are studio-local; the end hour is exclusive (12–16 means 12:00–15:59). A band can't cross midnight — enter 22–24 and 0–2 as two bands. Gaps are fine; check-ins outside every band count as Other.",
        )}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
        {t(
          'config.bands.instant',
          'Changing bands re-bands all history on the next view — no rebuild needed.',
        )}
      </Typography>

      {saveFailure !== null ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('config.bands.saveError', "Couldn't save attendance bands.")}
          <ErrorDetails
            error={saveFailure}
            headline={t('config.bands.saveError', "Couldn't save attendance bands.")}
          />
        </Alert>
      ) : null}
      {tooMany ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('config.bands.max', 'Up to 24 bands')}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        {drafts.map((draft, index) => {
          const labelErr = labelError(index);
          const hoursErr = hoursError(index);
          return (
            <Stack
              key={draft.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { sm: 'flex-start' } }}
              data-testid={`attendance-band-row-${index}`}
            >
              <TextField
                label={t('config.bands.label', 'Label')}
                value={draft.label}
                onChange={(event) => patchDraft(draft.id, { label: event.target.value })}
                slotProps={{ htmlInput: { maxLength: 60 } }}
                error={labelErr !== undefined}
                helperText={labelErr}
                sx={{ flex: 2, minWidth: 140 }}
              />
              <TextField
                select
                label={t('config.bands.from', 'From')}
                value={draft.fromHour ?? ''}
                onChange={(event) => {
                  const raw = event.target.value;
                  patchDraft(draft.id, {
                    fromHour: raw === '' ? null : Number(raw),
                  });
                }}
                error={hoursErr !== undefined}
                helperText={hoursErr}
                sx={{ flex: 1, minWidth: 100 }}
              >
                {HOUR_OPTIONS.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('config.bands.to', 'To')}
                value={draft.toHour ?? ''}
                onChange={(event) => {
                  const raw = event.target.value;
                  patchDraft(draft.id, {
                    toHour: raw === '' ? null : Number(raw),
                  });
                }}
                error={hoursErr !== undefined}
                sx={{ flex: 1, minWidth: 100 }}
              >
                {HOUR_OPTIONS.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                aria-label={t('config.bands.remove', 'Remove')}
                onClick={() => removeDraft(draft.id)}
                sx={{ mt: { sm: 1 } }}
              >
                <Icon name="close" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button onClick={addDraft} disabled={drafts.length >= MAX_BANDS}>
          {t('config.bands.add', 'Add band')}
        </Button>
        {drafts.length >= MAX_BANDS ? (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {t('config.bands.max', 'Up to 24 bands')}
          </Typography>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
        <Button variant="contained" disabled={saveDisabled} onClick={() => void onSave()}>
          {t('config.bands.save', 'Save')}
        </Button>
        <Button onClick={() => setResetOpen(true)} disabled={saving}>
          {t('config.bands.reset', 'Reset to default')}
        </Button>
      </Stack>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>
          {t('config.bands.resetConfirmTitle', 'Reset attendance bands?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'config.bands.resetConfirmBody',
              'This replaces your bands with the platform default. Changes take effect on the next dashboard view.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => void onResetConfirm()} color="primary">
            {t('config.bands.reset', 'Reset to default')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          {t('config.bands.saved', 'Attendance bands saved.')}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
